import { useState, type ReactNode } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSidebar } from "./SidebarContext";

interface SidebarCollapsibleSectionProps {
  label: string;
  children: ReactNode;
  onAdd?: () => void;
  defaultOpen?: boolean;
}

export function SidebarCollapsibleSection({
  label,
  children,
  onAdd,
  defaultOpen = true,
}: SidebarCollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { collapsed } = useSidebar();

  if (collapsed) {
    return (
      <div className="my-2 mx-2">
        <div className="h-px bg-border/40" />
        <div className="mt-2">{children}</div>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="group/section flex items-center justify-between px-4 pb-1.5">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.04em] text-muted hover:text-secondary transition-colors duration-150"
        >
          <ChevronRight
            size={11}
            strokeWidth={2.5}
            className={`transition-transform duration-200 ease-out ${open ? "rotate-90" : ""}`}
          />
          {label}
        </button>

        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="flex h-5 w-5 items-center justify-center rounded text-muted opacity-0 group-hover/section:opacity-100 hover:bg-card-hover/50 hover:text-secondary transition-all duration-150"
          >
            <Plus size={13} strokeWidth={2.5} />
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-px">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
